/**
 * 任意 JSON 值的读取工具（对应 Swift 的 JSONValue）。网关合约里大量字段是开放结构，
 * 这里所有读取都是“宽松”的：类型不对就当没有，不抛错。
 */
export type Json = null | boolean | number | string | Json[] | { [key: string]: Json };
export type JsonObject = { [key: string]: Json };

export function obj(v: unknown): JsonObject | undefined {
  return v !== null && typeof v === "object" && !Array.isArray(v) ? (v as JsonObject) : undefined;
}

export function arr(v: unknown): Json[] | undefined {
  return Array.isArray(v) ? (v as Json[]) : undefined;
}

/** 和 Swift 的 stringValue 一样：数字、布尔也转成字符串 */
export function str(v: unknown): string | undefined {
  if (typeof v === "string") return v;
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : String(v);
  if (typeof v === "boolean") return v ? "true" : "false";
  return undefined;
}

/** 只认真正的字符串（解码可选字符串字段时用） */
export function s(v: unknown): string | undefined {
  return typeof v === "string" ? v : undefined;
}

export function int(v: unknown): number | undefined {
  if (typeof v === "number" && Number.isFinite(v)) return Math.trunc(v);
  if (typeof v === "string" && /^-?\d+$/.test(v.trim())) return parseInt(v, 10);
  return undefined;
}

export function num(v: unknown): number | undefined {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "" && !Number.isNaN(Number(v))) return Number(v);
  return undefined;
}

export function bool(v: unknown): boolean | undefined {
  return typeof v === "boolean" ? v : undefined;
}

export function strings(v: unknown): string[] | undefined {
  const a = arr(v);
  return a ? a.filter((x): x is string => typeof x === "string") : undefined;
}

/** 构造参数对象，去掉 undefined / null（对应 Swift 的 .params） */
export function params(pairs: Record<string, Json | undefined>): JsonObject {
  const out: JsonObject = {};
  for (const [k, v] of Object.entries(pairs)) if (v !== undefined && v !== null) out[k] = v;
  return out;
}

export function prettyJSON(v: unknown): string {
  try {
    return JSON.stringify(sortKeys(v), null, 2) ?? "";
  } catch {
    return "";
  }
}

function sortKeys(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(sortKeys);
  const o = obj(v);
  if (!o) return v;
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(o).sort()) out[k] = sortKeys(o[k]);
  return out;
}
